import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Users,
  Package,
  ShieldCheck,
  BarChart3,
  FolderOpen,
  Search,
  Eye,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Menu,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import BlackLogo from "@/assets/images/logo/Black_Version.png";

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-6">
        <div className="flex h-20 items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center pt-4 pb-4">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={BlackLogo} alt="RealEsy" className="h-24" />
            </Link>
          </div>

          {/* Center: Navigation - Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Solutions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0">
                Solutions
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 p-2 shadow-xl border-gray-200">
                <div className="mb-2 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">For Teams</p>
                </div>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/solutions/brokerages" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">Brokerages</span>
                      <span className="text-xs text-gray-500">Manage signage at scale</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/solutions/agents" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">Agents</span>
                      <span className="text-xs text-gray-500">Order and track signs</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <div className="my-2 border-t border-gray-200"></div>
                <div className="mb-2 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">For Partners</p>
                </div>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-orange-50 transition-colors group"
                  asChild
                >
                  <Link to="/solutions/vendors" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-[#f58b01]">Vendors</span>
                      <span className="text-xs text-gray-500">Install & remove signs</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                {/* <DropdownMenuItem className="cursor-pointer py-3 px-3 rounded-lg hover:bg-orange-50 transition-colors group">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-[#f58b01]">
                        Signage Companies
                      </span>
                      <span className="text-xs text-gray-500">Fulfill orders efficiently</span>
                    </div>
                  </div>
                </DropdownMenuItem> */}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Features Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0">
                Features
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 p-2 shadow-xl border-gray-200">
                <div className="mb-2 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Core Operations</p>
                </div>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/installations" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">Installations</span>
                      <span className="text-xs text-gray-500">Schedule & track installations</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/removals" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">Removals</span>
                      <span className="text-xs text-gray-500">Manage sign removals</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/reporting" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">Reporting</span>
                      <span className="text-xs text-gray-500">Analytics & insights</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/workdrive" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">WorkDrive</span>
                      <span className="text-xs text-gray-500">Centralized file storage</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/vendormarketplace" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-blue-700">
                        Vendor Marketplace
                      </span>
                      <span className="text-xs text-gray-500">Connect & Shop Vendor Deals</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                {/* <div className="my-2 border-t border-gray-200"></div>
                <div className="mb-2 px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    AI-Powered Insights
                  </p>
                </div>
                <DropdownMenuItem
                  className="cursor-pointer py-3 px-3 rounded-lg hover:bg-indigo-50 transition-colors group"
                  asChild
                >
                  <Link to="/features/smartread" className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Search className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-indigo-700">
                        SmartRead AI
                      </span>
                      <span className="text-xs text-gray-500">Extract data from documents</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer py-3 px-3 rounded-lg hover:bg-indigo-50 transition-colors group">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-900 group-hover:text-indigo-700">
                        SmartShow AI
                      </span>
                      <span className="text-xs text-gray-500">Automated property insights</span>
                    </div>
                  </div>
                </DropdownMenuItem> */}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Pricing */}
            <Link
              to="/pricing"
              className="text-gray-700 hover:text-gray-900 transition-colors outline-none focus:outline-none focus-visible:outline-none"
            >
              Pricing
            </Link>
          </nav>

          {/* Right: CTA Buttons & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="hidden sm:flex">
              Login
            </Button>
            <Button asChild className="hidden md:flex bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
              <Link to="/contact">Get Started</Link>
            </Button>

            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden hover:bg-blue-50 transition-colors"
                >
                  <Menu className="h-6 w-6 text-gray-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] sm:w-[380px] p-0 overflow-y-auto">
                {/* Mobile Menu Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 p-6 pb-8">
                  <p className="text-white text-lg">Menu</p>
                  <p className="text-blue-100 text-sm mt-1">Navigate through our platform</p>
                </div>

                <nav className="flex flex-col px-4 py-6">
                  {/* Solutions Section */}
                  <Accordion type="single" collapsible className="w-full space-y-2">
                    <AccordionItem
                      value="solutions"
                      className="border-0 bg-gray-50 rounded-xl overflow-hidden"
                    >
                      <AccordionTrigger className="text-gray-900 hover:no-underline px-4 py-4 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium">Solutions</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="mb-2 px-3 pt-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">For Teams</p>
                        </div>
                        <div className="flex flex-col gap-1 pl-3">
                          <Link
                            to="/solutions/brokerages"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Building2 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Brokerages</span>
                              <span className="text-xs text-gray-500">Manage signage at scale</span>
                            </div>
                          </Link>
                          <Link
                            to="/solutions/agents"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Users className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Agents</span>
                              <span className="text-xs text-gray-500">Order and track signs</span>
                            </div>
                          </Link>
                        </div>
                        <div className="my-2 border-t border-gray-200"></div>
                        <div className="mb-2 px-3 pt-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            For Partners
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 pl-3">
                          <Link
                            to="/solutions/vendors"
                            className="flex items-center gap-3 text-gray-700 hover:text-[#f58b01] py-2.5 px-3 rounded-lg hover:bg-orange-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Package className="w-4 h-4 text-gray-400 group-hover:text-[#f58b01] transition-colors" />
                            <div className="flex flex-col">
                              <span>Vendors</span>
                              <span className="text-xs text-gray-500">Install & remove signs</span>
                            </div>
                          </Link>
                          <a
                            href="#signage"
                            className="flex items-center gap-3 text-gray-700 hover:text-[#f58b01] py-2.5 px-3 rounded-lg hover:bg-orange-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <ShieldCheck className="w-4 h-4 text-gray-400 group-hover:text-[#f58b01] transition-colors" />
                            <div className="flex flex-col">
                              <span>Signage Companies</span>
                              <span className="text-xs text-gray-500">
                                Fulfill orders efficiently
                              </span>
                            </div>
                          </a>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Features Section */}
                    <AccordionItem
                      value="features"
                      className="border-0 bg-gray-50 rounded-xl overflow-hidden"
                    >
                      <AccordionTrigger className="text-gray-900 hover:no-underline px-4 py-4 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-[#f58b01] to-orange-600 rounded-lg flex items-center justify-center">
                            <Package className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium">Features</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="mb-2 px-3 pt-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Core Operations
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 pl-3">
                          <Link
                            to="/features/installations"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Building2 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Installations</span>
                              <span className="text-xs text-gray-500">
                                Schedule & track installations
                              </span>
                            </div>
                          </Link>
                          <Link
                            to="/features/removals"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Building2 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Removals</span>
                              <span className="text-xs text-gray-500">Manage sign removals</span>
                            </div>
                          </Link>
                          <Link
                            to="/features/reporting"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <BarChart3 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Reporting</span>
                              <span className="text-xs text-gray-500">Analytics & insights</span>
                            </div>
                          </Link>
                          <Link
                            to="/features/workdrive"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <FolderOpen className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>WorkDrive</span>
                              <span className="text-xs text-gray-500">
                                Centralized file storage
                              </span>
                            </div>
                          </Link>
                          <Link
                            to="/features/vendormarketplace"
                            className="flex items-center gap-3 text-gray-700 hover:text-blue-600 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <ShieldCheck className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>Vendor Marketplace</span>
                              <span className="text-xs text-gray-500">
                                Connect & Shop Vendor Deals
                              </span>
                            </div>
                          </Link>
                        </div>
                        <div className="my-2 border-t border-gray-200"></div>
                        <div className="mb-2 px-3 pt-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            AI-Powered Insights
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 pl-3">
                          <Link
                            to="/features/smartread"
                            className="flex items-center gap-3 text-gray-700 hover:text-indigo-600 py-2.5 px-3 rounded-lg hover:bg-indigo-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Search className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>SmartRead AI</span>
                              <span className="text-xs text-gray-500">
                                Extract data from documents
                              </span>
                            </div>
                          </Link>
                          <a
                            href="#smartshow"
                            className="flex items-center gap-3 text-gray-700 hover:text-indigo-600 py-2.5 px-3 rounded-lg hover:bg-indigo-50 transition-all group"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Eye className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <div className="flex flex-col">
                              <span>SmartShow AI</span>
                              <span className="text-xs text-gray-500">
                                Automated property insights
                              </span>
                            </div>
                          </a>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Pricing Link */}
                  <Link
                    to="/pricing"
                    className="flex items-center justify-between text-gray-900 py-4 px-4 mt-2 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium">Pricing</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </Link>

                  {/* Mobile CTA Buttons */}
                  <div className="flex flex-col gap-3 pt-6 mt-6 border-t border-gray-200">
                    <Button
                      variant="outline"
                      className="w-full h-12 border-2 border-gray-300 hover:border-blue-600 hover:text-blue-600 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Button>
                    <Button
                      asChild
                      className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all"
                    >
                      <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                        Get Started
                      </Link>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
